import React, { useEffect, useState } from "react";
import MaestroLoader from "@components/MaestroLoader";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Sheet from '@mui/joy/Sheet';
import { Box, Button, ButtonGroup, Checkbox, Stack, Switch, Tooltip, Typography } from '@mui/joy';
import Dropdown from '@mui/joy/Dropdown';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemContent from '@mui/joy/ListItemContent';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import ListDivider from '@mui/joy/ListDivider';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import { useDocumentTitle } from '@uidotdev/usehooks';
import Subheader from '@components/layout/Subheader';
import { useAuth } from '@providers/AuthProvider';
import Table from '@mui/joy/Table';
import EnhancedTableHead from "@components/EnhancedTableHead";
import Paginate from "@components/Paginate";
import ToggleViewMode from "@components/ToggleViewMode";
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ObjectPreview from "./ObjectPreview";
import { useRestore } from "@api/crudService/useRestore";
import pluralize from 'pluralize';
import toast from "react-hot-toast";
import UnrecoverableError from "@components/UnrecoverableError";
import { CheckPermission } from "@components/CheckPermission";
import DeleteButton from "@components/DeleteButton";
import { FormProvider, useForm } from "react-hook-form";
import { DynamicControl } from "@components/forms/DynamicControl";
import { events } from "@utils/analytics";

function EditInlineButton({isEditing, ...props}) {
    return (
        <Tooltip title={isEditing ? 'Cancel Edit' : 'Edit'} disableInteractive>
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{
                    bgcolor: 'background.surface',
                }}
                {...props}
                >
                {isEditing && (<CloseRoundedIcon />)}
                {!isEditing && (<EditRoundedIcon />)}
            </IconButton>
        </Tooltip>
    );
}

function SaveInlineButton({mutation, ...props}) {
    return (
        <Tooltip title={'Save'} disableInteractive>
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={{
                    bgcolor: 'background.surface',
                }}
                {...props}
                >
                <SaveRoundedIcon />
            </IconButton>
        {/* </Link> */}
    </Tooltip>
    );
}


export default function ObjectInlineEdit({
    query,
    createMutation,
    updateMutation,
    deleteMutation,
    type = '',
    endpoint = '',
    permissionType = null,
    title,
    color,
    columns,
    listCell,
    listActions = () => {},
    defaultParams:_defaultParams = { order: 'asc', orderby: 'name', page: 1, view: 'active' },
}) {
    useDocumentTitle(`${title} | Maestro Community Manager`);

    const { navigate } = useNavigate();

    const [viewMode, _setViewMode] = useState('active');
    const [multiSelectEnabled, setMultiSelectEnabled] = useState(false);

    const [newItems, setNewItems] = useState([]);

    const defaultParams = {
        order: 'asc',
        orderby: 'name',
        page: 1,
        view: 'active',
        ..._defaultParams,
    };

    // todo: handle filters/sorts
    // let { order = 'asc', orderby = 'name', page = 1 } = useParams();
    const [searchParams, setSearchParams] = useSearchParams(defaultParams);

    // todo: probably move this to state w/ an effect, which would also solve pagination data undefined
    const order = searchParams.get('order') ?? defaultParams.order;
    const orderby = searchParams.get('orderby') ?? defaultParams.orderby;
    const page = searchParams.get('page') ?? defaultParams.page;
    // console.log(page, searchParams.get('page'), defaultParams.page);
    const view = searchParams.get('view') ?? defaultParams.view;

    // filter out columns that shouldn't display on deleted screen
    columns = columns.filter(column => {
        if(column?.showOnDeleted === false && view === 'deleted') {
            return false;
        }

        return true;
    });

    const { user, can } = useAuth();

    const {
        // isPending,
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
    } = query(
        {page, orderby, order, view},
        {
            retry: false,
        }
    );

    const handleRequestSort = (
        event,//: React.MouseEvent<unknown>,
        property,//: keyof Data,
    ) => {
        const isAsc = orderby === property && order === 'asc';

        setSearchParams({
            ...searchParams,
            page,
            view,
            order: isAsc ? 'desc' : 'asc',
            orderby: property,
        });

        // setOrder();
        // setOrderBy(property);
    };

    const setViewMode = () => {
        setMultiSelectEnabled(false);
        setSearchParams({
            ...searchParams,
            ...defaultParams,
            view: view === 'active' ? 'deleted' : 'active',
        });
    }

    const onSubmit = () => {
        console.log(arguments);
    }

    if(isLoading) {
        return (<>
            <Subheader
                title={title}
                subtitle={view === 'active' ? 'All' : 'Deleted'}
                tint={color}
            >
                {/* This icon button is used as placeholder to keep the page from changing height */}
                <IconButton
                    size="sm"
                    color="neutral"
                    variant="outlined"
                    // onClick={onClick}
                    sx={{ bgcolor: 'background.surface', opacity: 0, pointerEvents: 'none' }}
                >
                    <ChevronRightRoundedIcon />
                </IconButton>
            </Subheader>
            <Box sx={{p: 2, display: 'flex', justifyContent: 'center'}}><MaestroLoader centered/></Box>
        </>
        );
    }

    if(isError) {
        return (<UnrecoverableError status={403} />)
    }

    const LinkComponent = view === 'deleted' ? Box : ObjectPreview;

    const RestoreAction = ({row}) => {
        const navigate = useNavigate();
        let objectType = pluralize.singular(title).toLowerCase();// detectObjectType(row);
        // if(objectType === 'workplan') {
            // objectType = 'work-plan';
        // }
        // let endpoint = '';//pluralize(objectType);
        const restoreAction = useRestore({queryKey: [endpoint, row.id]});
        const onRestore = () => {
            const action = restoreAction.mutateAsync();
            toast.promise(action, {
                loading: 'Restoring...',
                success: (t) => (
                    <Box sx={{ marginRight: '-10px', display: 'flex', alignItems: 'center', gap: 1 }}>
                        The {objectType.replace('-', ' ')} was restored!
                        {/*
                        <br/>
                        <Button
                            size="sm"
                            variant="solid"
                            color="success"
                            // component={Link}
                            // to={`${row.id}`}
                            // relative={true}
                            onClick={() => {
                                toast.dismiss(t.id);
                                navigate(`${row.id}`);
                            }}
                        >
                            View
                        </Button>
                        */}
                    </Box>
                ),
                error: 'There was an error while restoring.',
            }, {
                success: {
                    duration: 5000,
                    // icon: '🔥',
                },
            });

            action.then(() => {
                events.objectRestored({type});
            });
        };
        return (
        // <LinkComponent to={`${row.id}`} relative={true}>
            <Tooltip title="Restore" placement="left">
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                onClick={onRestore}
                sx={{ bgcolor: 'background.surface' }}
                >
                <RestoreRoundedIcon />
            </IconButton>
            </Tooltip>
        // </LinkComponent>
    )
};

    const ViewAction = ({row}) => (
        <LinkComponent to={`${row.id}`} relative={true} object={row}>
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                // onClick={onClick}
                sx={{ bgcolor: 'background.surface' }}
                >
                <ChevronRightRoundedIcon />
            </IconButton>
        </LinkComponent>
    );

    const MoreAction = ({row}) => (
        <Dropdown>
            <MenuButton
                slots={{ root: IconButton }}
                slotProps={{ root: { variant: 'outlined', color: 'neutral', size: 'sm' } }}
            >
                <MoreVertRoundedIcon />
            </MenuButton>
            <Menu>
                <MenuItem component={Link} to={`${row.id}/edit`} relative={true}>Edit</MenuItem>
            </Menu>
        </Dropdown>
    );

    const CreateNewInlineButton = () => (
        <Tooltip title='Create New'>
            <IconButton
                size="sm"
                color="neutral"
                variant="outlined"
                sx={theme => ({
                    bgcolor: 'background.surface',
                    /*
                    bgcolor: theme.palette[props.color ?? "neutral"][600],// 'background.surface'
                    color: '#fff',
                    */
                })}
                onClick={() => {
                    setNewItems(v => [{}, ...v]);
                }}
                >
                <AddRoundedIcon />
            </IconButton>
        </Tooltip>
    );

    const EditableTableRow = ({row = {}, index = null, isNew = false}) => {
        const methods = useForm({
            defaultValues: row,
        });
        const { errors, isValid } = methods.formState;


        const [isEditing, setIsEditing] = useState(isNew); // todo: move this up because when the query refreshes it re-renders and cancels the editing
        // const [editData, setEditData] = useState(row);

        const mutation = isNew ? createMutation() : updateMutation({id: row.id});
        const deleteObject = deleteMutation({id: row?.id ?? 0});

        const onDelete = () => {
            setIsEditing(false);

            const performDelete = deleteObject.mutateAsync();

            toast.promise(performDelete, {
                loading: 'Deleting...',
                success: `The ${pluralize.singular(title).toLowerCase()} was deleted.`,
                error: 'There was an error while deleting.',
            });

            performDelete.then(() => {
                events.objectDeleted({type});
            });
        }

        const onSubmit = () => {
            const performUpdate = mutation.mutateAsync(methods.watch());

            toast.promise(performUpdate, {
                loading: `${isNew?'Creating':'Saving'}...`,
                success: `The ${pluralize.singular(title).toLowerCase()} was ${isNew?'created':'saved'}!`,
                error: `There was an error while ${isNew?'creating':'saving'}.`,
            });

            performUpdate.then((newData) => {
                if(isNew) {
                    events.objectCreated({type});
                } else {
                    events.objectEdited({type});
                }

                setIsEditing(false);

                let _newItems = [...newItems];
                _newItems.splice(index, 1);
                setNewItems(_newItems);
                // todo: figure out how to update the object (or auto-invalidation handles it?)
            });
        }


        const hasAsterisk = (field) => !!(field?.config?.required) || field.hasOwnProperty('showRequired');
        // const showAsterisk = !!(row?.config?.required) || row.hasOwnProperty('showRequired') && row.showRequired(getValues());

        return (
            <FormProvider {...methods}>
            <tr key={row.id}>
            {columns.map((column, index) => (
                <td key={index} style={{verticalAlign: isEditing ? 'top' : 'middle'}}>
                    {!isEditing && column.cell(row, {LinkComponent})}
                    {isEditing && <>
                        {column?.showLabels !== false && (
                        <Typography>
                            {column?.tooltip ?? column?.label}
                            {hasAsterisk(column) && <Typography color="danger" fontSize={'xs'} sx={{position: 'relative', left: '.13rem', top: '-0.25rem'}}>*</Typography>}
                        </Typography>
                        )}
                        <DynamicControl name={column.id} {...column} placeholder={column?.placeholder ?? `${column.label}...`} />
                        {!!column.additionalEditFields && (
                            <Stack sx={{mt: 1}}>
                                {column.additionalEditFields.map((field, fieldIndex) => (<React.Fragment key={fieldIndex}>
                                    {column?.showLabels !== false && (
                                    <Typography>
                                        {field?.tooltip}
                                        {hasAsterisk(field) && <Typography color="danger" fontSize={'xs'} sx={{position: 'relative', left: '.13rem', top: '-0.25rem'}}>*</Typography>}
                                    </Typography>
                                    )}
                                    <Tooltip title={!column?.showLabels && field?.tooltip}>
                                        <Box sx={{mb: 0.5}}>
                                            <DynamicControl name={field.id} inputLabel={field.label} {...field} />
                                        </Box>
                                    </Tooltip>
                                    </React.Fragment>))}
                            </Stack>
                        )}
                    </>}
                </td>
            ))}
            <td style={{verticalAlign: isEditing ? 'top' : 'middle'}}>
                <CheckPermission permissions={permissionType === null || `edit ${permissionType}`}>
                    {view === 'deleted' && (<RestoreAction row={row}/>)}
                    {view !== 'deleted' && (<>
                        <ButtonGroup
                            size="sm"
                            color="neutral"
                            variant="outlined"
                            orientation="vertical"
                            sx={{justifyContent: 'flex-end'}}
                        >
                            {isEditing == true && (
                                <SaveInlineButton
                                    disabled={!isValid}
                                    // onClick={methods.handleSubmit(onSubmit)}
                                    onClick={() => {
                                        onSubmit();
                                        /*
                                        console.log('test');
                                        methods.handleSubmit(onSubmit)();
                                        */
                                    }}
                                />
                            )}
                            <EditInlineButton isEditing={isEditing} onClick={()=>{
                                setIsEditing(e=>!e)
                                // delete it if we cancel editing

                                if(isNew && index !== null) {
                                    let _newItems = [...newItems];
                                    _newItems.splice(index, 1);
                                    setNewItems(_newItems);
                                }
                            }} />
                            {isNew === false && isEditing === true && (
                                <DeleteButton onDelete={onDelete} />
                            )}
                        </ButtonGroup>
                    </>)}
                </CheckPermission>
            </td>
        </tr>
        </FormProvider>);
    }


    // const PrimaryTableAction = ({row}) => view === 'deleted' ? <RestoreAction row={row}/> : <MoreAction row={row}/>;
    // const PrimaryListAction = ({row}) => view === 'deleted' ? <RestoreAction row={row}/> : <ViewAction row={row}/>;

    const PrimaryTableAction = view === 'deleted' ? RestoreAction : MoreAction;
    const PrimaryListAction = view === 'deleted' ? RestoreAction : ViewAction;

    return (<>
        <Subheader
            title={title}
            subtitle={view === 'active' ? 'All' : 'Deleted'}
            tint={color}
        >
            <Paginate
                tint={color}
                from={data.from}
                to={data.to}
                total={data.total}
                currentPage={data.current_page}
                perPage={data.per_page}
                lastPage={data.last_page}
                onChange={(newPage) => {
                    setSearchParams({
                        ...searchParams,
                        page: newPage,
                        order,
                        orderby,
                        view,
                    });
                }}
            />
            <CheckPermission permissions={`edit ${permissionType}`}>
                <Stack sx={{gap: 1, flexDirection: 'row'}}>
                    <ButtonGroup
                        size="sm"
                        color="neutral"
                        variant="outlined"
                    >
                            <ToggleViewMode current={view} onChange={setViewMode} />
                    </ButtonGroup>
                </Stack>

                <CreateNewInlineButton /*onClick={addRow}*/ />
            </CheckPermission>
        </Subheader>

        {/* Desktop Table */}
        <Box sx={theme => ({
            backgroundImage: color && `linear-gradient(180deg, ${theme.palette[color][100]} 0%, white 100%);`,// : null,
            backgroundAttachment: 'fixed',
            minHeight: 'calc(100vh - var(--Header-height) - 100px)',
            p: 2,
        })}>
        <Sheet
            variant="outlined"
            sx={{
                display: { xs: 'none', sm: 'block' },
                // width: '100%',
                borderRadius: 'sm',
                flexShrink: 1,
                overflow: 'auto',
                minHeight: 0,
                // m: 2,
                bgcolor: 'white',
            }}
        >
            <Table
                aria-labelledby="tableTitle"
                stickyHeader
                // hoverRow
                stripe={'even'}
                sx={{
                    '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                    '--Table-headerUnderlineThickness': '1px',
                    '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                    '--TableRow-stripeBackground': 'var(--joy-palette-background-level1)',
                    '--TableCell-paddingY': '4px',
                    '--TableCell-paddingX': '8px',
                    bgcolor: 'white',
                    tableLayout: 'auto',
                }}
            >
                <EnhancedTableHead
                    numSelected={0}//{selected.length}
                    order={order}
                    orderBy={orderby}
                    onSelectAllClick={()=>{}}
                    onRequestSort={handleRequestSort}
                    rowCount={data.data.length}
                    headCells={columns}
                    multiSelect={multiSelectEnabled}
                />
                <tbody>
                    {data.data.length === 0 && newItems.length === 0 && (
                        <tr>
                            <td colSpan={columns.length+1}>
                                <Typography fontSize={'sm'}>There is nothing here yet.</Typography>
                            </td>
                        </tr>
                    )}
                    {newItems.map((row, index) => (<EditableTableRow key={`new-${index}`} index={index} isNew/>))}
                    {data.data.map(row => <EditableTableRow key={row.id} row={row}/>)}
                </tbody>
            </Table>
        </Sheet>
        </Box>

        {/* Mobile List */}
        <Box sx={{ m:2, display: { xs: 'block', sm: 'none' } }}>
            {data.data.map((row) => (
                <List
                    key={row.id}
                    size="sm"
                    sx={{
                        '--ListItem-paddingX': 0,
                    }}
                >
                <ListItem
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                    }}
                >
                    <ListItemContent sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {/*
                        <ListItemDecorator sx={{flex: '0 0 auto'}}>
                            {/*  multiselect checkbox here *\/}
                        </ListItemDecorator>
                        */}

                        <div style={{flex: '1 1 auto'}}>
                        {listCell(row, {LinkComponent})}
                        </div>
                        <ListItemDecorator sx={{flex: '0 0 auto', gap: 1, '--ListItem-gap': 0}}>
                            {listActions(row, {view, LinkComponent})}
                            <PrimaryListAction row={row} />
                        </ListItemDecorator>
                    </ListItemContent>
                </ListItem>
                <ListDivider />
                </List>
            ))}
        </Box>
    </>);
}
